import { LIVE_TICK_MS, type RateLimit } from '@mixwire/contracts';

import { useNow } from '../hooks/useNow.js';

import type { ReactElement } from 'react';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

// Shown so a stalled poll doesn't read as a bug or the DJ walking off: the
// wait is Spotify's own Retry-After plus a margin, and the refusal is the
// app's, not one DJ's, so every channel shows it at once.
const HTTP_CAT_429_URL = 'https://http.cat/429';
const HTTP_CAT_429_ALT = 'HTTP 429 — Too Many Requests';

const clockTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const waitLabel = (retryAt: string, nowMs: number): string => {
  const seconds = Math.max(0, Math.ceil((Date.parse(retryAt) - nowMs) / MS_PER_SECOND));
  if (seconds === 0) {
    return 'any moment now';
  }
  if (seconds < SECONDS_PER_MINUTE) {
    return `in ${String(seconds)}s`;
  }
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  return `in ${String(minutes)}m ${String(seconds % SECONDS_PER_MINUTE)}s`;
};

// Null is a normal state — Spotify is answering — so both notices render
// nothing for it and no caller needs a conditional of its own.
type RateLimitProps = {
  readonly rateLimit: RateLimit | null;
};

export const RateLimitLine = ({ rateLimit }: RateLimitProps): ReactElement | null => {
  const nowMs = useNow(LIVE_TICK_MS);

  if (rateLimit === null) {
    return null;
  }

  return (
    <div role="status" className="mt-2">
      <p className="text-danger text-xs">
        Spotify rate limited at {clockTime(rateLimit.since)} · following resumes{' '}
        {waitLabel(rateLimit.retryAt, nowMs)}
      </p>
      <img
        src={HTTP_CAT_429_URL}
        alt={HTTP_CAT_429_ALT}
        className="border-subtle rounded-control mt-2 max-h-28 w-full border object-cover"
      />
    </div>
  );
};

export const RateLimitBanner = ({ rateLimit }: RateLimitProps): ReactElement | null => {
  const nowMs = useNow(LIVE_TICK_MS);

  if (rateLimit === null) {
    return null;
  }

  return (
    <div role="status" className="bg-danger-subtle rounded-card p-4 text-center">
      <p className="text-danger text-sm font-semibold">
        Spotify is rate limiting us — since {clockTime(rateLimit.since)}
      </p>
      <p className="text-secondary mt-1 text-xs">
        Nothing has been read from Spotify since, on any channel, so the numbers below are as of
        then. The quota is the whole app's rather than yours alone, which is why every channel has
        stopped together. The poll is waiting out the delay Spotify asked for and picks up again{' '}
        {waitLabel(rateLimit.retryAt, nowMs)} on its own — there is nothing to press.
      </p>
      <img
        src={HTTP_CAT_429_URL}
        alt={HTTP_CAT_429_ALT}
        className="border-subtle rounded-control mx-auto mt-3 max-h-48 w-auto border"
      />
    </div>
  );
};
